import React, { FC } from 'react'
import { useHistory } from 'react-router-dom'
import LaneArrowIcon from 'transfix-icons/build/LaneArrowIcon'
import { Button } from 'transfix-ui/components/Button'
import { FormattedDateTime } from 'transfix-ui/components/FormattedDateTime'
import { TableRow, TableCell } from 'transfix-ui/components/Table'
import { Typography, Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme/colors'
import { getMonthDuration } from 'apps/fleet-planner/utils/dedicatedLanesHelpers'
import { DedicatedLanesResponse } from 'apps/marketplace/apiTypes/dedicatedLanes'
import { formatCityState } from 'utils/formatCityState'
import { MARKETPLACE_EQUIPMENT_TYPES } from 'constants/equipment'

export interface ILaneOfferRowProps {
  dedicatedLane: DedicatedLanesResponse
}

export interface IDedicatedLanesOfferRowProps {
  id?: number
  equipmentName?: string
  pickupCity?: string | null
  pickupStateCode?: string | null
  pickupLoadingMethod: string
  deliveryCity?: string | null
  deliveryStateCode?: string | null
  deliveryLoadingMethod: string
  monthDuration?: number
  startDate: string
  endDate: string
  volume?: number | null
}

export const getDedicatedLaneInfo = (
  dedicatedLane: DedicatedLanesResponse
): IDedicatedLanesOfferRowProps => {
  const {
    delivery,
    endDate = '',
    equipment,
    id,
    loadingMethod = '',
    pickup,
    startDate = '',
    volume,
  } = dedicatedLane || {}
  const { parentId: equipmentId } = equipment || {}
  const equipmentName = MARKETPLACE_EQUIPMENT_TYPES.find(eq => eq.id === equipmentId)?.name || ''
  const { city: pickupCity, stateCode: pickupStateCode } = pickup || {}
  const { city: deliveryCity, stateCode: deliveryStateCode } = delivery || {}
  const pickupLoadingMethod = loadingMethod.split('-')[0] || ''
  const deliveryLoadingMethod = loadingMethod.split('-')[1] || ''

  const monthDuration = getMonthDuration(dedicatedLane)

  return {
    equipmentName,
    pickupCity,
    pickupStateCode,
    pickupLoadingMethod,
    deliveryCity,
    deliveryStateCode,
    deliveryLoadingMethod,
    monthDuration,
    startDate,
    endDate,
    id,
    volume,
  }
}

export const parseLoadingMethod = (loadingMethod: string): string => {
  switch (loadingMethod) {
    case 'live':
      return 'Live Load'
    case 'drop':
      return 'Drop Trailer'
    default:
      return ''
  }
}

const useLaneOfferRowStyles = makeStyles(() => ({
  row: {
    '&:hover, &:focus': {
      backgroundColor: brandColors.coolGray1,
      cursor: 'pointer',
    },
  },
  mobileRow: {
    display: 'block!important',
  },
}))

export const DedicatedLanesOfferRow: FC<ILaneOfferRowProps> = ({ dedicatedLane }) => {
  const history = useHistory()
  const {
    deliveryCity,
    deliveryLoadingMethod,
    deliveryStateCode,
    endDate,
    equipmentName,
    id,
    monthDuration,
    pickupCity,
    pickupLoadingMethod,
    pickupStateCode,
    startDate,
    volume,
  } = getDedicatedLaneInfo(dedicatedLane) || {}

  const openOfferDetailsHandler = () => {
    history.push(`/dedicated-lanes/lane/${id}`)
  }
  const classes = useLaneOfferRowStyles()

  return (
    <TableRow className={classes.row} onClick={openOfferDetailsHandler}>
      <TableCell colSpan={2}>
        <Box pl={2}>
          <Typography variant='subtitle1' data-test='lane-offer-row-pickup'>
            {formatCityState(pickupCity || '', pickupStateCode || '')}
          </Typography>
          <Typography
            variant='body2'
            color='textSecondary'
            data-test='lane-offer-row-pickup-loading-method'>
            {parseLoadingMethod(pickupLoadingMethod)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell colSpan={1}>
        <LaneArrowIcon size='large' />
      </TableCell>
      <TableCell colSpan={2}>
        <Box pl={2}>
          <Typography variant='subtitle1' data-test='lane-offer-row-delivery'>
            {formatCityState(deliveryCity || '', deliveryStateCode || '')}
          </Typography>
          <Typography
            variant='body2'
            color='textSecondary'
            data-test='lane-offer-row-delivery-loading-method'>
            {parseLoadingMethod(deliveryLoadingMethod)}
          </Typography>
        </Box>
      </TableCell>
      <TableCell colSpan={3}>
        <Box pl={3}>
          <Typography display='inline' variant='subtitle1' data-test='lane-offer-row-volume'>
            {volume} loads
          </Typography>
          <Typography display='inline' color='textSecondary'>
            {' '}
            / week
          </Typography>
          <Typography variant='body2' color='textSecondary'>
            {equipmentName}
          </Typography>
        </Box>
      </TableCell>
      <TableCell colSpan={4}>
        <Box pl={2}>
          <Typography variant='subtitle1'>
            <FormattedDateTime
              displayTimezone='America/New_York'
              datetime={startDate}
              format='dateAndYear'
            />
            {' - '}
            <FormattedDateTime
              displayTimezone='America/New_York'
              datetime={endDate}
              format='dateAndYear'
            />
          </Typography>
          {monthDuration && (
            <Typography variant='body2' color='textSecondary'>
              {monthDuration} month(s)
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell colSpan={3} className='pr4 tr'>
        <Button color='tertiary' onClick={openOfferDetailsHandler}>
          View Details
        </Button>
      </TableCell>
    </TableRow>
  )
}
